import { useProjectData } from "@/contexts/ProjectData"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"
import { useLocalGeospatialData } from "@/hooks/useLocalGeospatialData"
import { GeospatialLocationSelector } from "@/components/geospatial/GeospatialLocationSelector"
import { FormExitGuard } from "@/components/common/FormExitGuard"
import { PhotosForm } from "@/components/photos/PhotosForm"
import {
  PhotoChangesProvider,
  usePhotoChanges,
} from "@/components/photos/PhotosFormProvider"
import {
  useDraftRestore,
  useDraftRestoredNotice,
  useFormDraft,
} from "@/hooks/useFormDraft"
import { useHasUnsavedChanges } from "@/hooks/useHasUnsavedChanges"
import { usePhotoMutations } from "@/hooks/usePhotoMutations"
import { usePhotosForEntity } from "@/hooks/usePhotosForEntity"
import { draftPhotoToStaged } from "@/lib/drafts/draftPhotos"
import { createDiscardCoordinator } from "@/lib/drafts/discardCoordinator"
import { saveDraftForExit } from "@/lib/drafts/saveDraftForExit"
import { buildResumeKey } from "@/lib/drafts/types"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useState } from "react"
import { useForm } from "react-hook-form"
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema"
import { Badge } from "@fieldbook/ui/badge"
import { Button } from "@fieldbook/ui/button"
import { Input } from "@fieldbook/ui/input"
import { Label } from "@fieldbook/ui/label"
import { Spinner } from "@fieldbook/ui/spinner"
import { Textarea } from "@fieldbook/ui/textarea"
import { ColorPicker } from "@fieldbook/ui/color-picker"
import { DEFAULT_GEOSPATIAL_COLOR } from "@fieldbook/common/utils"
import type { LocalGeospatialDataRecord } from "@fieldbook/common/types"
import { MapPin, Shapes } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import z from "zod"
import {
  buildEditFormDefaults,
  canEditGeometry,
  canEditRecord,
  geometryChanged,
  geospatialEditFormSchema,
  GEOMETRY_TYPE_LABELS,
  isLocationGeometryType,
  isLocationShape,
  LOCATION_GEOMETRY_TYPES,
  type GeospatialEditDraft,
  type GeospatialEditFormValues,
  type LocationGeometryType,
} from "./-geospatialForm"

const searchParamSchema = z.object({
  draftId: z.string().uuid().optional(),
})

export const Route = createFileRoute(
  "/_private/projects/$id/geospatial/$recordId/edit",
)({
  component: EditGeospatialRecordRoute,
  validateSearch: searchParamSchema.parse,
})

/**
 * Resolves the stored record, its photos and any autosaved draft before the
 * form mounts. react-hook-form freezes its defaults at mount, so all three
 * have to settle first — hence the separate component rather than a branch
 * inside EditGeospatialRecord.
 */
function EditGeospatialRecordRoute() {
  // This route covers the project map for its whole life, loading states
  // included. Releasing the map's WebGL context and tile buffers here keeps
  // the process small enough to survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId, recordId } = Route.useParams()
  const { draftId } = Route.useSearch()
  const { user, role } = useAuth()
  const { getRecord, isLoading } = useLocalGeospatialData(projectId)
  const { data: photos, isLoading: photosLoading } = usePhotosForEntity({
    entityId: recordId,
    entityType: "geospatial_data",
  })

  const resumeKey = buildResumeKey({
    kind: "geospatial",
    projectId,
    mode: "edit",
    entityId: recordId,
  })

  const restore = useDraftRestore<GeospatialEditFormValues, null>(
    resumeKey,
    user?.id ?? null,
    draftId,
  )

  const record = getRecord(recordId)

  // Wait for photos too: PhotoChangesProvider seeds its kept-photo state once,
  // so mounting it before they arrive would mark every photo for removal.
  if (isLoading || photosLoading || restore.status === "pending") {
    return (
      <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!record) {
    return (
      <div className="bg-background absolute inset-0 z-20 p-4">
        Record not found
      </div>
    )
  }

  if (
    !canEditRecord({
      currentUserId: user?.id,
      creatorId: record.created_by,
      role,
    })
  ) {
    return (
      <div className="bg-background absolute inset-0 z-20 p-4">
        Only the creator of this record or an admin can edit it.
      </div>
    )
  }

  return (
    <PhotoChangesProvider
      initialPhotos={photos ?? []}
      restoredKeep={restore.draft?.existingPhotos ?? undefined}
      initialStagedPhotos={restore.draft?.photos.map(draftPhotoToStaged)}
    >
      <EditGeospatialRecord
        record={record}
        resumeKey={resumeKey}
        restoredDraft={restore.draft}
      />
    </PhotoChangesProvider>
  )
}

function EditGeospatialRecord({
  record,
  resumeKey,
  restoredDraft,
}: {
  record: LocalGeospatialDataRecord
  resumeKey: string
  restoredDraft: GeospatialEditDraft | null
}) {
  const { id: projectId, recordId } = Route.useParams()
  const { draftId } = Route.useSearch()
  const navigate = useNavigate()
  const {
    data: { project },
  } = useProjectData()
  const { user, org } = useAuth()
  const { updateRecord } = useLocalGeospatialData(projectId)
  const [photoChanges] = usePhotoChanges()
  const photoMutations = usePhotoMutations(
    recordId,
    "geospatial_data",
    org?.id ?? "",
  )

  // Multi-part geometries from imports have no editor here; the form edits
  // everything else and leaves the geometry as it is.
  const geometryEditable = canEditGeometry(record)

  const form = useForm<GeospatialEditFormValues>({
    defaultValues: buildEditFormDefaults({ record, draft: restoredDraft }),
    resolver: standardSchemaResolver(geospatialEditFormSchema),
    mode: "all",
  })

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form

  const draft = useFormDraft({
    resumeKey,
    kind: "geospatial",
    projectId,
    ownerId: user?.id ?? null,
    entityId: recordId,
    label: watch("name") || record.name,
    form,
    extras: null,
    photos: photoChanges.add,
    existingPhotos: photoChanges.keep,
    restoredDraft,
    // Autosaving over a form that is already committing would race the discard
    // that follows a successful save.
    enabled: Boolean(user) && !isSubmitting,
  })

  useDraftRestoredNotice(restoredDraft, "geospatial record")

  const [showLocationSelector, setShowLocationSelector] = useState(false)
  const [notesFocus, setNotesFocus] = useState(false)
  const geometryType = watch("geometry_type")
  const geometry = watch("geojson")
  const color = watch("color")

  // Only narrowed when the geometry is editable; the selector is not rendered
  // otherwise.
  const selectorType = isLocationGeometryType(geometryType)
    ? geometryType
    : undefined
  const selectorValue = isLocationShape(geometry) ? geometry : undefined

  const handleGeometryTypeChange = useCallback(
    (type: LocationGeometryType) => {
      setValue("geometry_type", type, { shouldDirty: true })
      // A geometry drawn as one shape isn't a valid value for another.
      setValue("geojson", undefined, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )

  const onSubmit = useCallback(
    async (data: GeospatialEditFormValues) => {
      clearErrors("root")

      try {
        if (!user) throw new Error("Not logged in")
        if (!org) throw new Error("No organisation selected")

        // Only send the geometry when it was changed, so an edit to the name
        // doesn't queue the whole shape for upload again.
        const shouldWriteGeometry =
          geometryEditable && geometryChanged(record, data)

        await updateRecord(recordId, {
          name: data.name,
          notes: data.notes.trim() || null,
          color: data.color,
          updated_at: new Date().toISOString(),
          ...(shouldWriteGeometry &&
            data.geojson !== undefined && {
              geometry_type: data.geometry_type,
              geojson: data.geojson,
            }),
        })

        // The record write above was awaited, so only the photo batch is still
        // outstanding. The draft holds the only copy of those bytes until they
        // land, and must survive if any of them fails.
        const discard = createDiscardCoordinator(["photos"], draft.discard)
        photoMutations.applyChanges(photoChanges, {
          onAllSettled: (allSucceeded) =>
            discard.settled("photos", allSucceeded),
        })

        navigate({
          to: "/projects/$id/data/geospatial/$recordId",
          params: { id: projectId, recordId },
        })
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Something went wrong while saving the record"
        setError("root", { message })
      }
    },
    [
      user,
      org,
      geometryEditable,
      record,
      updateRecord,
      recordId,
      projectId,
      navigate,
      photoChanges,
      photoMutations,
      draft,
      setError,
      clearErrors,
    ],
  )

  const hasUnsavedChanges = useHasUnsavedChanges({ form })

  // Leaving only: FormExitGuard discards the draft first.
  const handleCancel = useCallback(() => {
    navigate({
      to: "/projects/$id/data/geospatial/$recordId",
      params: { id: projectId, recordId },
    })
  }, [navigate, projectId, recordId])

  const handleSaveDraft = useCallback(
    () =>
      saveDraftForExit({
        validateIdentifier: () => true,
        save: draft.saveForExit,
      }),
    [draft],
  )

  if (!project) return null

  const geometryLabel =
    geometry !== undefined
      ? `${geometryType} location selected - tap to edit`
      : `Select ${geometryType} location`

  return (
    <div className="bg-background absolute inset-0 z-20 flex flex-col justify-between gap-2 pb-2">
      <div className="space-y-2 overflow-y-scroll">
        <div className="flex items-center p-2 text-2xl">
          <Shapes className="mr-2 size-6" />
          Edit Geospatial Record
        </div>

        <div className="flex flex-col gap-4 px-1">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="name">Name *</Label>
            <Input
              {...register("name")}
              id="name"
              className="h-12 text-lg"
              autoComplete="off"
              placeholder="Enter a name"
            />
            {errors.name && (
              <div className="mt-1 text-sm text-red-600">
                {errors.name.message}
              </div>
            )}
          </div>

          {geometryEditable ? (
            <>
              {/* Geometry Type */}
              <div className="space-y-1">
                <Label>Geometry Type</Label>
                <div className="flex gap-2">
                  {LOCATION_GEOMETRY_TYPES.map((value) => (
                    <Button
                      key={value}
                      type="button"
                      variant={geometryType === value ? "default" : "outline"}
                      onClick={() => handleGeometryTypeChange(value)}
                      className="flex-1"
                      size="sm"
                    >
                      {GEOMETRY_TYPE_LABELS[value]}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="space-y-1">
                <Label>Location</Label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowLocationSelector(true)}
                  className="h-12 w-full justify-start text-lg"
                >
                  <MapPin className="mr-2 size-5" />
                  {geometryLabel}
                </Button>
                {errors.geojson && (
                  <div className="mt-1 text-sm text-red-600">
                    {errors.geojson.message}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-1">
              <Label>Geometry Type</Label>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{record.geometry_type}</Badge>
                <span className="text-muted-foreground text-sm">
                  This geometry can't be edited on the device.
                </span>
              </div>
            </div>
          )}

          {/* Colour */}
          <div className="space-y-1">
            <Label>Colour</Label>
            <div className="flex items-center gap-2">
              <ColorPicker
                value={color}
                fallbackColor={DEFAULT_GEOSPATIAL_COLOR}
                label="Colour for geospatial record"
                onChange={(value) =>
                  setValue("color", value, { shouldDirty: true })
                }
              />
              {color && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue("color", null, { shouldDirty: true })}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              {...register("notes")}
              id="notes"
              className={`h-20 text-lg transition-all duration-500 ease-in-out ${
                notesFocus ? "h-40" : ""
              }`}
              placeholder="Enter notes"
              onFocus={() => setNotesFocus(true)}
              onBlur={() => setNotesFocus(false)}
            />
          </div>

          {/* Photos */}
          <PhotosForm
            entityType="geospatial_data"
            onCaptureStateChange={draft.setCaptureInFlight}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 border-t border-green-800 px-1 pt-2">
        {errors.root?.message && (
          <div className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-950">
            {errors.root.message}
          </div>
        )}
        <FormExitGuard
          hasUnsavedChanges={hasUnsavedChanges}
          discardDraft={draft.discard}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
          isEditingDraft={Boolean(draftId && restoredDraft?.savedByUser)}
        />
        <Button
          type="button"
          disabled={isSubmitting}
          className="h-12 w-full text-base"
          onClick={handleSubmit(onSubmit)}
        >
          {isSubmitting ? "Saving..." : "Update Record"}
        </Button>
      </div>

      {/* Location Selector Overlay */}
      {showLocationSelector && selectorType && (
        <GeospatialLocationSelector
          isOpen={showLocationSelector}
          onClose={() => setShowLocationSelector(false)}
          locationType={selectorType}
          project={project}
          value={selectorValue}
          onChange={(g) =>
            setValue("geojson", g, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            })
          }
        />
      )}
    </div>
  )
}
